import Icon from "@/components/mnf/Icon";
import type { SafetyAdvisory, SafetySeverity } from "@/lib/store";

/**
 * SafetyBadge
 *
 * Compact pill shown on RecipeCard when a recipe has season-relevant safety
 * advisories. Tone follows severity: gold for caution, coral for avoid.
 * Always informational, never alarming. Single chip if one advisory; "+N"
 * suffix if there are multiple.
 */
export default function SafetyBadge({
  advisories,
  size = "sm",
}: {
  advisories: SafetyAdvisory[];
  size?: "sm" | "md";
}) {
  if (advisories.length === 0) return null;

  const topSeverity: SafetySeverity = advisories.some((a) => a.severity === "avoid")
    ? "avoid"
    : "caution";

  const palette =
    topSeverity === "avoid"
      ? {
          bg: "rgba(192,120,74,0.16)",
          color: "var(--mnf-t)",
          border: "1px solid rgba(192,120,74,0.32)",
          icon: "warning",
        }
      : {
          bg: "rgba(160,120,32,0.14)",
          color: "var(--mnf-gold)",
          border: "1px solid rgba(160,120,32,0.28)",
          icon: "info",
        };

  const first = advisories[0];
  const remaining = advisories.length - 1;
  const label = remaining > 0 ? `${first.label} +${remaining}` : first.label;

  return (
    <span
      data-testid={`badge-safety-${topSeverity}`}
      className="inline-flex items-center gap-1 mnf-body"
      style={{
        background: palette.bg,
        color: palette.color,
        border: palette.border,
        padding: size === "sm" ? "3px 8px" : "5px 11px",
        borderRadius: 999,
        fontSize: size === "sm" ? 10.5 : 11.5,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      <Icon name={palette.icon} size={size === "sm" ? 11 : 13} />
      {label}
    </span>
  );
}
